import { SVButton, SVCheckbox, SVForm } from '@/components/core';
import { MESSAGES } from '@/constants/messages';
import { useFileTaxDocument } from '@/hooks/requests/useBankAccounts';
import { TaxDocumentItem, TaxDocumentStatus } from '@/types/bankAccounts';
import showToast from '@/utils/toast';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

type TaxDocumentsManageButtonProps = {
  taxDocument: TaxDocumentItem;
};

export default function TaxDocumentsManageButton({
  taxDocument,
}: TaxDocumentsManageButtonProps) {
  const { uuid, url, status, name } = taxDocument;
  const { fileTaxDocument, isFileTaxDocumentPending } =
    useFileTaxDocument(uuid);

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const formMethods = useForm({
    defaultValues: { confirm: false },
    mode: 'onBlur',
  });

  async function handleFileTaxDocument() {
    try {
      await fileTaxDocument();
      setOpen(false);
      showToast({
        type: 'success',
        data: {
          content: `${name} has been successfully filed`,
        },
      });
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <>
      <SVButton onClick={handleClick}>Manage</SVButton>
      <Menu
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              minWidth: 150,
            },
          },
        }}>
        <MenuItem onClick={() => window.open(url, '_blank')}>Download</MenuItem>
        {status === TaxDocumentStatus.PendingReview && (
          <MenuItem onClick={() => setOpen(true)}>Verify</MenuItem>
        )}
      </Menu>
      <Dialog fullWidth open={open} onClose={() => setOpen(false)}>
        <SVForm onSubmit={handleFileTaxDocument} methods={formMethods}>
          <DialogContent>
            <Typography variant="h6" component="h2">
              Confirm Tax Document Filing
            </Typography>
            <SVCheckbox
              label="I have verified the tax document for Filing with the IRS"
              name="confirm"
              rules={{ required: true }}
            />
          </DialogContent>
          <DialogActions>
            <SVButton onClick={() => setOpen(false)} variant="tertiary">
              Cancel
            </SVButton>
            <SVButton
              isLoading={isFileTaxDocumentPending}
              onClick={formMethods.handleSubmit(handleFileTaxDocument)}>
              Submit
            </SVButton>
          </DialogActions>
        </SVForm>
      </Dialog>
    </>
  );
}
